const ApiError = require('@/lib/Api/ApiError');
const NetworkApiError = require('@/lib/Api/NetworkApiError');
const NotFoundApiError = require('@/lib/Api/NotFoundApiError');

/**
 * Convert an error from an API request to an ApiError.
 * This supports both angular-resource and Axios errors.
 *
 * In angular-resource requests you can drop this in as a catch handler like this:
 * someResource.get(
 *    {},
 *    (result) => // ...,
 *    (err) => reject(apiResponseToError(err))
 * );
 *
 * @param {{}} result
 *
 * @returns {ApiError}
 */
const apiResponseToError = (result) => {
    // Default error message.
    let message = null;

    // Extract the response data and status from the result object.
    let responseData = null;
    let statusCode = null;
    let statusText = null;
    let rawResponse = null;

    if (result && result.response) {
        // This looks like an Axios error.
        let response = result.response;
        responseData = response.data;
        statusCode = response.status;
        statusText = response.statusText;
        rawResponse = response;
    } else if (result && typeof result === 'object') {
        // This looks like an angular-resource error.
        if (result.data) {
            responseData = result.data;
        }
        if (result.status) {
            statusCode = result.status;
        }
        if (result.statusText) {
            statusText = result.statusText;
        }
        rawResponse = result;
    } else if (result && typeof result === 'string') {
        message = result;
    }

    if (responseData && responseData.error) {
        message = responseData.error;
    } else if (responseData && responseData.message) {
        message = responseData.message;
    }

    // If there was an error message found in the response use just that.
    // Otherwise, create a default error message which will include the HTTP status.
    if (!message) {
        message = 'API request failed';

        // Append the HTTP status code and status text to the message.
        const statusString = [
            statusCode,
            statusText,
        ].filter((val) => val && (typeof val === 'string' || typeof val === 'number')).join(' ');

        if (statusString) {
            message += ` (${statusString})`;
        }
    }

    if (statusCode === 404) {
        return new NotFoundApiError(message, responseData, rawResponse);
    }

    return new ApiError(message, statusCode, responseData, rawResponse);
};

/** @typedef {import('axios').AxiosError} AxiosError */
/** @typedef {import('axios').AxiosResponse} AxiosResponse */

/**
 * @param {AxiosError} err
 *
 * @returns {NetworkApiError|ApiError}
 */
const axiosErrorToApiError = (err) => {
    let error;
    if (err.response) {
        // Server responded with an error (e.g. 4xx or 5xx)
        error = axiosResponseToApiError(err.response);
    } else {
        // Network error: no response was received from the server
        error = new NetworkApiError(err.message);
    }

    if (err.config) {
        error.setRequestConfig(err.config);
    }

    return error;
};

/**
 * @param {AxiosResponse} response
 *
 * @returns {NotFoundApiError|ApiError}
 */
const axiosResponseToApiError = (response) => {
    // Extract the response data and status from the result object.
    let responseData = response.data;
    let statusCode = response.status;
    let statusText = response.statusText; // Only used in the default error message.

    let message;
    if (responseData?.error) {
        // Try to get a message from the error property in the response.
        message = responseData.error;
    } else if (responseData?.message) {
        // Try to get a message from the message property in the response.
        message = responseData.message;
    } else {
        // Construct a default message.
        // e.g. "API request failed (500 Internal Server Error)"
        message = 'API request failed';

        // Append the HTTP status code and status text to the default message.
        const statusString = [
            statusCode,
            statusText,
        ].filter((val) => val && (typeof val === 'string' || typeof val === 'number')).join(' ');

        if (statusString) {
            message += ` (${statusString})`;
        }
    }

    let err;
    if (statusCode === 404) {
        err = new NotFoundApiError(message, responseData, response);
    } else {
        err = new ApiError(message, statusCode, responseData, response);
    }

    return err;
};

module.exports = {
    apiResponseToError,
    axiosErrorToApiError,
    axiosResponseToApiError,
};
